/**
 * Runtime configuration — docs/spec/01 §6.
 *
 * This is the ONLY place `import.meta.env` may be read (root CLAUDE.md rule;
 * enforced by ESLint). Every host, base path, and key reaches the app through
 * the frozen `config` object below, so the exact prod/staging paths — still
 * open as Q1 — can change without touching a single feature.
 *
 * Parsing fails loudly at startup and reports EVERY problem at once: a
 * misconfigured deploy should tell you all of what is wrong in one go, not
 * one key per redeploy.
 */

import { parseDsn } from '../api/errorReporting'

export const APP_ENVS = ['local', 'staging', 'production'] as const
export type AppEnv = (typeof APP_ENVS)[number]

export interface AppConfig {
  /** Go physical-mail api base. Absolute URL, or an absolute path when proxied (09 §2). */
  readonly apiBase: string
  /** Central Pigeonly api base (countries; auth issuer pending Q2). */
  readonly coreApiBase: string
  /**
   * Live-queue socket. `null` when unset — the app then runs fully on
   * HTTP + polling. WebSockets are an enhancement, never required (03 §1).
   */
  readonly wsUrl: string | null
  /**
   * Haystac facility-address lookup base (02 §3, Q4 closed 2026-07-29).
   * `null` disables facility search — the recipient block then cannot get a
   * `facility_address_id`, so the form says so rather than failing at submit.
   */
  readonly haystacBase: string | null
  /** Referrer-restricted Google Maps JS key; `null` disables address autocomplete. */
  readonly mapsKey: string | null
  /**
   * GitLab error-tracking DSN (06 §6, Q11 resolved 2026-07-29). `null` disables
   * reporting entirely, which is the local default — a developer's own stack
   * traces are on their screen already.
   */
  readonly errorDsn: string | null
  readonly env: AppEnv
}

export class ConfigError extends Error {
  readonly problems: readonly string[]

  constructor(problems: readonly string[]) {
    super(
      `Invalid runtime configuration (docs/spec/01 §6):\n` +
        problems.map((p) => `  - ${p}`).join('\n'),
    )
    this.name = 'ConfigError'
    this.problems = problems
  }
}

/** Source shape: `import.meta.env` in the app, a literal in tests. */
export type RawEnv = Readonly<Record<string, string | undefined>>

function isAbsoluteHttpUrl(value: string): boolean {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  return url.protocol === 'http:' || url.protocol === 'https:'
}

/**
 * The scheme `new WebSocket()` needs, keyed by the scheme an operator wrote.
 *
 * `https://host/api/ws` is the natural way to write a socket endpoint — it is
 * how the origin is spelled everywhere else, and it is what the deploy
 * variables carry. But `new WebSocket('https://…')` throws a `SyntaxError`, so
 * accepting it verbatim would ship a socket that can never open.
 */
const WEBSOCKET_SCHEME: Readonly<Record<string, string>> = {
  'http:': 'ws:',
  'https:': 'wss:',
  'ws:': 'ws:',
  'wss:': 'wss:',
}

/**
 * Read `VITE_WS_URL`, converting an http(s) origin to its ws(s) equivalent.
 *
 * Normalising rather than rejecting is deliberate: WebSockets are an
 * enhancement that the app must run without (03 §1), so a scheme character in
 * a deploy variable must never be able to fail `parseConfig` and take down
 * every screen — including the ones that have nothing to do with the socket.
 * Anything that is not an http/ws URL at all is still a hard problem, because
 * that is a genuine misconfiguration nobody should discover from a silent
 * absence of live updates.
 */
function readWebSocketUrl(raw: RawEnv, problems: string[]): string | null {
  const value = readOptional(raw, 'VITE_WS_URL')
  if (value === null) return null

  let url: URL
  try {
    url = new URL(value)
  } catch {
    problems.push(`VITE_WS_URL must be a ws(s):// or http(s):// URL when set, got "${value}"`)
    return null
  }

  const scheme = WEBSOCKET_SCHEME[url.protocol]
  if (scheme === undefined) {
    problems.push(`VITE_WS_URL must be a ws(s):// or http(s):// URL when set, got "${value}"`)
    return null
  }

  // Both http(s) and ws(s) are "special" schemes, so this conversion is legal
  // per the URL standard and rewrites nothing else about the URL.
  url.protocol = scheme
  return url.toString()
}

/** A base may be an absolute http(s) URL or a root-relative path (proxied dev). */
function readBase(raw: RawEnv, key: string, problems: string[]): string {
  const value = raw[key]?.trim()
  if (!value) {
    problems.push(`${key} is required`)
    return ''
  }
  if (!value.startsWith('/') && !isAbsoluteHttpUrl(value)) {
    problems.push(`${key} must be an absolute http(s) URL or a root-relative path, got "${value}"`)
    return ''
  }
  // Normalise away a trailing slash so callers can always join with "/path".
  return value.endsWith('/') ? value.slice(0, -1) : value
}

function readOptional(raw: RawEnv, key: string): string | null {
  const value = raw[key]?.trim()
  return value ? value : null
}

export function parseConfig(raw: RawEnv): AppConfig {
  const problems: string[] = []

  const apiBase = readBase(raw, 'VITE_API_BASE', problems)
  const coreApiBase = readBase(raw, 'VITE_CORE_API_BASE', problems)

  const wsUrl = readWebSocketUrl(raw, problems)

  /**
   * Validated like `VITE_WS_URL`: absent means "reporting off", but a value that
   * is not a usable DSN is a **deploy mistake**, and the failure mode without
   * this check is the worst kind — every error silently going nowhere, in the one
   * environment where nobody is watching a console.
   */
  const errorDsn = readOptional(raw, 'VITE_ERROR_DSN')
  if (errorDsn !== null && parseDsn(errorDsn) === null) {
    problems.push(`VITE_ERROR_DSN must be a Sentry-style DSN when set, got "${errorDsn}"`)
  }

  const envRaw = raw['VITE_ENV']?.trim()
  if (!envRaw) {
    problems.push('VITE_ENV is required')
  } else if (!(APP_ENVS as readonly string[]).includes(envRaw)) {
    problems.push(`VITE_ENV must be one of ${APP_ENVS.join(' | ')}, got "${envRaw}"`)
  }

  if (problems.length > 0) throw new ConfigError(problems)

  return Object.freeze({
    apiBase,
    coreApiBase,
    wsUrl,
    haystacBase: readOptional(raw, 'VITE_HAYSTAC_BASE'),
    mapsKey: readOptional(raw, 'VITE_MAPS_KEY'),
    errorDsn,
    env: envRaw as AppEnv,
  })
}

/** True when the live-queue socket is configured (03 §1). */
export function isWebSocketEnabled(cfg: AppConfig): boolean {
  return cfg.wsUrl !== null
}

/** True when sender-address autocomplete can run (05 §3). */
export function isAddressAutocompleteEnabled(cfg: AppConfig): boolean {
  return cfg.mapsKey !== null
}

let cached: AppConfig | null = null

/**
 * The app's configuration, parsed once from `import.meta.env`.
 *
 * Deliberately lazy: importing this module has no side effects, so a test can
 * import `parseConfig` without a fully-populated environment. `main.tsx` calls
 * this first thing, which is what makes a bad deploy fail loudly at startup
 * rather than at the first request.
 */
export function getConfig(): AppConfig {
  cached ??= parseConfig(import.meta.env)
  return cached
}

/** Test-only: drop the memoised config so a case can vary the environment. */
export function resetConfigForTests(): void {
  cached = null
}
